import { useCallback, type KeyboardEvent, type ReactNode } from 'react'
import { Pressable } from './Pressable'

export interface SegmentedControlOption<T extends string = string> {
  value: T
  label: string
  ariaLabel?: string
  disabled?: boolean
  icon?: ReactNode
}

export interface SegmentedControlProps<T extends string = string> {
  options: Array<SegmentedControlOption<T>>
  value: T
  onChange: (value: T) => void
  ariaLabel: string
  name?: string
  size?: 'sm' | 'md'
  disabled?: boolean
  className?: string
  testId?: string
}

/**
 * SegmentedControl — an accessible two-or-more option toggle with role="radiogroup".
 * Used for mutual choices such as Direction ("Take out" / "Put back").
 */
export function SegmentedControl<T extends string = string>({
  options,
  value,
  onChange,
  ariaLabel,
  size = 'md',
  disabled = false,
  className,
  testId,
}: SegmentedControlProps<T>) {
  const enabledOptions = options.filter((opt) => !opt.disabled && !disabled)

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
      if (disabled || enabledOptions.length === 0) return

      let nextIndex = -1
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        event.preventDefault()
        nextIndex = (currentIndex + 1) % options.length
        while (options[nextIndex]?.disabled && nextIndex !== currentIndex) {
          nextIndex = (nextIndex + 1) % options.length
        }
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        event.preventDefault()
        nextIndex = (currentIndex - 1 + options.length) % options.length
        while (options[nextIndex]?.disabled && nextIndex !== currentIndex) {
          nextIndex = (nextIndex - 1 + options.length) % options.length
        }
      } else if (event.key === 'Home') {
        event.preventDefault()
        nextIndex = options.findIndex((opt) => !opt.disabled)
      } else if (event.key === 'End') {
        event.preventDefault()
        for (let i = options.length - 1; i >= 0; i--) {
          if (!options[i].disabled) {
            nextIndex = i
            break
          }
        }
      }

      if (nextIndex >= 0 && nextIndex < options.length && !options[nextIndex].disabled) {
        onChange(options[nextIndex].value)
      }
    },
    [disabled, enabledOptions.length, onChange, options],
  )

  const isSm = size === 'sm'

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      data-testid={testId}
      className={`inline-flex items-center p-0.5 rounded-lg border border-[var(--border)] bg-[var(--surface-raised)] select-none ${
        disabled ? 'opacity-50 pointer-events-none' : ''
      } ${className ?? ''}`}
    >
      {options.map((option, index) => {
        const isSelected = option.value === value
        const isDisabled = disabled || Boolean(option.disabled)

        return (
          <Pressable
            key={option.value}
            role="radio"
            aria-checked={isSelected}
            aria-label={option.ariaLabel ?? option.label}
            disabled={isDisabled}
            tabIndex={isSelected ? 0 : -1}
            onClick={() => {
              if (!isDisabled && !isSelected) {
                onChange(option.value)
              }
            }}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className={`inline-flex items-center justify-center gap-1.5 transition-all rounded-md font-medium ${
              isSm ? 'h-6 px-2.5 text-xs' : 'h-7 px-3 text-xs'
            } ${
              isSelected
                ? 'bg-[var(--surface-card)] text-[var(--text-strong)] font-semibold shadow-sm'
                : 'text-[var(--muted)] hover:text-[var(--text-strong)]'
            } ${isDisabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'} focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-1`}
          >
            {option.icon ? (
              <span className="inline-flex items-center justify-center" aria-hidden="true">
                {option.icon}
              </span>
            ) : null}
            <span>{option.label}</span>
          </Pressable>
        )
      })}
    </div>
  )
}
